// frontend/src/content/modules.js
//
// THE single source of module identity. Every user-visible module name in the
// app comes from here — the sidebar (Layout.js), the page heading (PageHeader),
// and any future help/checklist surface.
//
// `roles` feeds BOTH the sidebar filter and the route guard (RequireModule in
// App.js). That is deliberate: nav visibility and route authorisation used to be
// two independent lists, and they drifted — managers saw three links that
// bounced them home. With one list they cannot disagree.
//
// Array order IS display order. Group order and item order within a group both
// derive from position below; nothing sorts.
//
// Adding a module: add an entry here (NOT to a nav array in Layout.js), add the
// matching <Route> in App.js, and use <PageHeader moduleKey="..."> on the page.

const ALL_ROLES = ['admin', 'dept_head', 'manager', 'employee'];
const MANAGER_LIKE = ['admin', 'dept_head', 'manager'];

// Program ids. The two existing programs; a department binds users to one.
const TECH_PROGRAM_ID  = 1;
const SALES_PROGRAM_ID = 2;

/**
 * key       — stable id; used by PageHeader and later the help page. Not user-visible.
 * label     — the canonical name. The ONLY source for sidebar text and page heading.
 * route     — path, matching App.js.
 * icon      — key into navIcons.js.
 * group     — sidebar group heading; null renders the item ungrouped at the top.
 * purpose   — one sentence. Default page subtitle; later the help-page blurb.
 * roles     — roles that may reach the route. Drives nav visibility AND the guard.
 * programId — null = every program; otherwise visible only on that program.
 */
export const MODULES = [
  {
    key: 'home',
    label: 'Overview',
    route: '/',
    icon: 'grid',
    group: null,
    purpose: 'Your current cycle at a glance — what you owe and what comes next.',
    roles: ALL_ROLES,
    programId: null,
  },

  // ── Company: shared context about the org and the program it runs ──────────
  {
    key: 'program-guide-tech',
    label: 'Program guide',
    route: '/program',
    icon: 'trophy',
    group: 'Company',
    purpose: 'How the Tech & Product recognition and incentive program works — cycles, scoring, and rewards.',
    roles: ALL_ROLES,
    programId: TECH_PROGRAM_ID,
  },
  {
    key: 'program-guide-sales',
    label: 'Program guide',
    route: '/program/sales',
    icon: 'trophy',
    group: 'Company',
    purpose: 'How the Sales Team program works — action plans, journals, and alignment sessions.',
    roles: ALL_ROLES,
    programId: SALES_PROGRAM_ID,
  },
  {
    key: 'guide',
    label: 'Guide',
    route: '/guide',
    icon: 'book',
    group: 'Company',
    purpose: 'How to use this system, and the order things have to happen in.',
    roles: ALL_ROLES,
    programId: null,
  },
  {
    key: 'org-chart',
    label: 'Org chart',
    route: '/org',
    icon: 'org',
    group: 'Company',
    purpose: 'Reporting lines and access levels across the organisation.',
    roles: ALL_ROLES,
    programId: null,
  },

  // ── Sales Team ────────────────────────────────────────────────────────────
  {
    key: 'sales-action-plans',
    label: 'Action plans',
    route: '/sales/action-plans',
    icon: 'plan',
    group: 'Sales Team',
    purpose: 'Your three-month plan per cycle — targets, pipeline, activity, and support.',
    roles: ALL_ROLES,
    programId: SALES_PROGRAM_ID,
  },
  {
    key: 'sales-journal',
    label: 'Weekly journal',
    route: '/sales/journal',
    icon: 'journal',
    group: 'Sales Team',
    purpose: 'A short weekly record of progress, reflections, and learnings.',
    roles: ALL_ROLES,
    programId: SALES_PROGRAM_ID,
  },
  {
    key: 'sales-gas',
    label: 'GAS sessions',
    route: '/sales/gas',
    icon: 'gas',
    group: 'Sales Team',
    purpose: 'Group Alignment Sessions — shared agendas and notes, tied to a cycle.',
    roles: ALL_ROLES,
    programId: SALES_PROGRAM_ID,
  },

  // ── Performance ───────────────────────────────────────────────────────────
  {
    key: 'reviews',
    label: 'Performance reviews',
    route: '/reviews',
    icon: 'list',
    group: 'Performance',
    purpose: 'Narrative performance reviews and calibration ratings, per cycle.',
    roles: ALL_ROLES,
    programId: null,
  },
  {
    key: 'kpi-scoring',
    label: 'KPI scoring',
    route: '/evaluations',
    icon: 'kpi',
    group: 'Performance',
    purpose: 'Score people 1–5 against their assigned KPIs, each cycle.',
    roles: ALL_ROLES,
    programId: null,
  },
  {
    key: 'okrs',
    label: 'OKRs & goals',
    route: '/okrs',
    icon: 'target',
    group: 'Performance',
    purpose: 'Organisation and individual objectives, tracked by review cycle.',
    roles: ALL_ROLES,
    programId: null,
  },
  {
    key: 'checkins',
    label: 'Check-ins',
    route: '/checkins',
    icon: 'chat',
    group: 'Performance',
    purpose: '1:1 check-ins and review sessions — scheduled, completed, and overdue.',
    roles: ALL_ROLES,
    programId: null,
  },

  // ── Growth ────────────────────────────────────────────────────────────────
  {
    key: 'feedback',
    label: '360 feedback',
    route: '/feedback',
    icon: 'star',
    group: 'Growth',
    purpose: 'Peer, manager, and upward feedback.',
    roles: ALL_ROLES,
    programId: null,
  },
  {
    key: 'career',
    label: 'Career ladder',
    route: '/career',
    icon: 'chart',
    group: 'Growth',
    purpose: 'Progression tracks and development plans.',
    roles: ALL_ROLES,
    programId: null,
  },

  // ── Manage: not "Admin" — managers read three of these ────────────────────
  {
    key: 'people',
    label: 'Manage users',
    route: '/admin/users',
    icon: 'admin',
    group: 'Manage',
    purpose: 'Add people and set their role, department, reporting line, and KPI role type.',
    roles: MANAGER_LIKE,
    programId: null,
  },
  {
    key: 'role-types',
    label: 'Role types',
    route: '/admin/role-types',
    icon: 'badge',
    group: 'Manage',
    purpose: 'The role types that determine which KPI templates a person receives.',
    roles: MANAGER_LIKE,
    programId: null,
  },
  {
    key: 'review-cycles',
    label: 'Review cycles',
    route: '/reviews/cycles',
    icon: 'cycle',
    group: 'Manage',
    purpose: 'Create cycles, set milestone deadlines, and open or close them.',
    roles: MANAGER_LIKE,
    programId: null,
  },
  {
    key: 'kpi-setup',
    label: 'KPI setup',
    route: '/kpi-setup',
    icon: 'kpi',
    group: 'Manage',
    purpose: 'Define and weight the KPIs each role is measured on.',
    roles: MANAGER_LIKE,
    programId: null,
  },
  {
    key: 'departments',
    label: 'Departments',
    route: '/admin/departments',
    icon: 'dept',
    group: 'Manage',
    purpose: 'Departments and the performance program each one follows.',
    roles: ['admin'],
    programId: null,
  },
];

const BY_KEY   = new Map(MODULES.map(m => [m.key, m]));
const BY_ROUTE = new Map(MODULES.map(m => [m.route, m]));

/**
 * The module for a key. THROWS on an unknown key rather than degrading to a
 * blank heading — moduleKey values are static strings written once per page, and
 * modules.test.js asserts every literal in pages/ resolves, so a typo cannot
 * reach production.
 */
export function moduleByKey(key) {
  const m = BY_KEY.get(key);
  if (!m) throw new Error(`Unknown module key "${key}" — add it to content/modules.js`);
  return m;
}

/** The module owning a route, or undefined for a non-registry route. */
export function moduleByRoute(route) {
  return BY_ROUTE.get(route);
}

/**
 * Roles that may reach a route. Undefined for a non-registry route, which
 * RequireModule treats as a programming error.
 */
export function rolesForRoute(route) {
  return BY_ROUTE.get(route)?.roles;
}

/**
 * Sidebar contents for a user: role match, plus program gating. Preserves
 * registry array order, which is the display order.
 */
export function visibleModules(user, activeProgramId) {
  const role = user?.role;
  const program = Number(activeProgramId) || Number(user?.program_id) || null;
  return MODULES.filter(m => {
    if (!role || !m.roles.includes(role)) return false;
    if (m.programId == null) return true;
    return Number(m.programId) === program;
  });
}

/**
 * visibleModules grouped for rendering, in array order. Ungrouped items
 * (group === null) come back under a null key, which Layout renders with no
 * heading above it.
 */
export function visibleModuleGroups(user, activeProgramId) {
  const groups = [];
  const index = new Map();
  for (const m of visibleModules(user, activeProgramId)) {
    if (!index.has(m.group)) {
      const entry = { group: m.group, items: [] };
      index.set(m.group, entry);
      groups.push(entry);
    }
    index.get(m.group).items.push(m);
  }
  return groups;
}
